import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990TInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 990-T Filing Instructions for 2025"
          description="Learn how to file IRS Form 990-T, including required details, credits, deductions, and tax computation for tax-exempt organizations and trusts."
          keywords=""
          canonical="resources/tax-exempt/form-990-t-instructions/"
          ogType="article"
          jsonLdPricing="9.90"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 990-T</span>
          </span>{' '}
          Instructions: Filing Guide for Exempt Organizations
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Complete IRS instructions to understand and file Form 990-T for
          unrelated business income accurately.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Tinstructions`}
          >
            E-file Now & Stay Exempt
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/tax-exempt/form-990-t-instructions/"
                      />

                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-07-20T09:00:00-05:00"
                          >
                            July 20, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-07-20T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            July 20, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Peter Samuel
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Form 990-T at a Glance
                      </Typography>
                      <Typography
                        variant="body1"
                        itemProp="description"
                        sx={{ mb: 2 }}
                      >
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{ color: '#0202ff', textDecoration: 'underline' }}
                        >
                          IRS Form 990-T
                        </Link>{' '}
                        is the annual tax return used by tax-exempt
                        organizations to report and pay tax on{' '}
                        <b>unrelated business taxable income (UBTI)</b>.
                        Organizations with <b>gross income of $1,000 or more</b>{' '}
                        from an unrelated trade or business generally must file
                        Form 990-T. The IRS Form 990-T can be filed along with{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                          sx={{ color: '#0202ff', textDecoration: 'underline' }}
                        >
                          Form 990
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                          sx={{ color: '#0202ff', textDecoration: 'underline' }}
                        >
                          Form 990-EZ
                        </Link>{' '}
                        or{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-pf/"
                          sx={{ color: '#0202ff', textDecoration: 'underline' }}
                        >
                          Form 990-PF
                        </Link>
                        .
                      </Typography>
                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',
                          '& li': { mb: 1.6, position: 'relative', pl: 3 },
                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li>
                          <span>
                            <Link
                              href="/resources/tax-exempt/form-990-due-dates/"
                              sx={{
                                color: '#0202ff',
                                textDecoration: 'underline',
                              }}
                            >
                              <b>Due Date:</b>
                            </Link>{' '}
                            Most exempt organizations file by the{' '}
                            <b>15th day of the 5th month</b> after the tax year
                            ends, while employee trusts generally file by the{' '}
                            <b>15th day of the 4th month.</b>
                          </span>
                        </li>

                        <li>
                          <span>
                            <Link
                              href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                              sx={{
                                color: '#0202ff',
                                textDecoration: 'underline',
                              }}
                            >
                              <b>Extension:</b>
                            </Link>{' '}
                            File <b>IRS Form 8868</b> by the original due date
                            to receive an automatic <b>6-month extension.</b>
                          </span>
                        </li>

                        <li>
                          <span>
                            <Link
                              href="/resources/tax-exempt/form-990-penalties/"
                              sx={{
                                color: '#0202ff',
                                textDecoration: 'underline',
                              }}
                            >
                              <b>Late Filing Penalties:</b>
                            </Link>{' '}
                            Late or incomplete returns may result in IRS
                            penalties and interest, depending on the tax due.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Box
                      component="div"
                      sx={{ mb: 5, p: 2, borderRadius: 2 }}
                      className="gradient-bg"
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontSize: '18px !important',
                              fontWeight: 600,
                              mb: 1,
                              lineHeight: 1.5,
                            }}
                          >
                            Simplify your 990-T filing with TaxZerone.
                            <br /> Apply code{' '}
                            <Box
                              component="strong"
                              sx={{
                                ml: 0,
                                color: '#c02652',
                                fontWeight: 600,
                                border: '2px dashed rgb(211, 163, 33)',
                                px: 1,
                                backgroundColor: 'rgb(255, 250, 205)',
                              }}
                            >
                              TZOTE10
                            </Box>{' '}
                            at checkout!
                          </Typography>
                          <Typography variant="body2">
                            We support tax years 2025, and 2024.
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: 200,
                            textAlign: 'center',
                          }}
                        >
                          <Button
                            variant="contained"
                            sx={{
                              textTransform: 'none',
                              boxShadow: 'none',
                              borderRadius: 2,
                              backgroundColor: '#fff',
                              color: '#333',
                              '&:hover, &:focus, &:active': { color: '#fff' },
                            }}
                            size="large"
                            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Tinstructions`}
                          >
                            E-file Now
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Who must file Form 990-T?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      Form 990-T is filed by{' '}
                    </Typography>
                    <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                      <li>
                        Any disregarded entity, domestic or foreign organization
                        exempt under section 501(a), section 529(a) or section
                        529A(a){' '}
                      </li>
                      <li>
                        Colleges and Universities of states and other
                        governmental units{' '}
                      </li>
                      <li>Qualified tuition programs under section 529 </li>
                      <li>Qualified ABLE programs under section 529A </li>
                      <li>
                        Trustees of Individual Retirement Accounts (IRAs),
                        simplified employee pension (SEP IRAs), SIMPLE IRAs,
                        Roth IRAs, Coverdell ESAs, Archer MSAs, Health savings
                        Account (HSA).{' '}
                      </li>
                    </Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Due date for filing Form 990-T
                    </Typography>
                    <Box component="div" sx={{ mb: 4 }}>
                      <TableContainer sx={{ border: '1px solid #828282' }}>
                        <Table sx={{}} aria-label="Due Date Chart">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#ccc' }}>
                              <TableCell sx={{ color: '#444', width: 300 }}>
                                Organization Types
                              </TableCell>

                              <TableCell sx={{ color: '#444', width: 300 }}>
                                Form 990-T Due Date
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow sx={{}}>
                              <TableCell>
                                <ul>
                                  <li style={{ marginBottom: 16 }}>
                                    Employees trust under section 401(a){' '}
                                  </li>
                                  <li style={{ marginBottom: 16 }}>
                                    IRAs (including SEPs and SIMPLEs)
                                  </li>
                                  <li style={{ marginBottom: 16 }}>
                                    Roth IRAs
                                  </li>
                                  <li style={{ marginBottom: 16 }}>
                                    Coverdell ESAs
                                  </li>
                                  <li style={{ marginBottom: 16 }}>
                                    Archer MSAs
                                  </li>
                                </ul>{' '}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <span style={{ fontWeight: 600 }}>
                                  {' '}
                                  April 15, 2026
                                </span>{' '}
                                <br /> (15th day of 4th month after the end of
                                tax year){' '}
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                              <TableCell> All other organizations </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <span style={{ fontWeight: 600 }}>
                                  {' '}
                                  May 15, 2026
                                </span>{' '}
                                <br /> (15th day of 5th month after the end of
                                tax year){' '}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                    <Box
                      component="div"
                      sx={{ mb: 5, p: 2, borderRadius: 2 }}
                      className="gradient-bg"
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontSize: '18px !important',
                              fontWeight: 600,
                              mb: 1,
                              lineHeight: 1.5,
                            }}
                          >
                            Need an additional time to file your return?
                          </Typography>
                          <Typography variant="body2">
                            File Form 8868 and get an automatic 6- month
                            extension
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            width: '100%',
                            maxWidth: 200,
                            textAlign: 'center',
                          }}
                        >
                          <Button
                            variant="contained"
                            sx={{
                              textTransform: 'none',
                              boxShadow: 'none',
                              borderRadius: 2,
                              backgroundColor: '#fff',
                              color: '#333',
                              '&:hover, &:focus, &:active': { color: '#fff' },
                            }}
                            size="large"
                            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Tinstructions`}
                          >
                            E-File Form 8868
                          </Button>
                        </Box>
                      </Stack>
                    </Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      The following information is required to complete and file
                      IRS Form 990-T:
                    </Typography>
                    <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                      <li>
                        The organization&apos;s name, EIN, address, and phone
                        number.{' '}
                      </li>
                      <li>
                        Relevant information on any address changes, name
                        changes, or amended returns.{' '}
                      </li>
                      <li>Group exemption number. </li>
                      <li>Organization&apos;s tax-exempt status. </li>
                      <li>
                        If you are only filing{' '}
                        <Link
                          href="/tax-exempt/e-file-irs-form-990-t/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Form 990-T
                        </Link>{' '}
                        to claim a credit from Form 8941 and/or to claim a
                        refund shown on Form 2439, be sure to provide the
                        relevant details.
                      </li>
                      <li>Form of organization. </li>
                      <li>
                        The fair market value of all assets at the end of the
                        year.{' '}
                      </li>
                      <li>Schedule A details attached with Form 990-T. </li>
                    </Box>
                  </Box>
                </Stack>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  Items A through L
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990-t-business-tax-img.webp" />
                  <source srcSet="/assets/images/990-t-business-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990-t-business-tax-img.png"
                    loading="lazy"
                    alt="Organization details of 990-T"
                    width={755}
                    height={329}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Item A - Address Change
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the organization has changed its address since it last
                  filed a return, check item A.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  If a change in address occurs after the return is filed, you
                  must use Form 8822-B, Change of Address or Responsible Party —
                  Business, to notify the IRS of the new address.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Item B - Tax Exemption
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the box under which the organization receives its tax
                  exemption.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Qualified pension, profit-sharing, and stock bonus plans
                    should check the 501 box and enter “a” between the first set
                    of parentheses. Do not make an entry in the space between
                    the second parentheses.{' '}
                  </li>
                  <li>
                    For other organizations exempt under section 501, check the
                    box for 501 and enter the section that describes their
                    tax-exempt status, for example, 501(c)(3).{' '}
                  </li>
                  <li>
                    For tax-exempt organizations that don&apos;t receive their
                    exemption under section 501, use the following guide.{' '}
                  </li>
                </Box>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            IF you are a
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            THEN check this box
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>IRA, SEP, or SIMPLE </TableCell>
                          <TableCell>408(e) </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Roth IRA </TableCell>
                          <TableCell>408A</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Archer MSA</TableCell>
                          <TableCell>220(e)</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Coverdell ESA </TableCell>
                          <TableCell>530(a)</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Qualified State Tuition Program{' '}
                          </TableCell>
                          <TableCell>529(a)</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Qualified ABLE Program </TableCell>
                          <TableCell>529A</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Public colleges and universities that have not obtained
                  recognition of exemption under section 501(c)(3), and section
                  6417(d)(1)(A) applicable entities that are not described in
                  section 501(a) should not check any box in item B.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Item C -</span> Enter here
                  the total of the end-of-year assets from the
                  organization&apos;s books of account.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Item D - Employer identification number{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter your organisation’s employer identification number
                  (EIN).{' '}
                </Typography>
                <Typography variant="body1">
                  An EIN may be applied for in one of the following ways.
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Online. Go to{' '}
                    <Link
                      href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      IRS.gov/EIN
                    </Link>
                    . The EIN is issued immediately once the application
                    information is validated.
                  </li>
                  <li>
                    By mailing or faxing Form SS-4, Application for Employer
                    Identification Number.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Only organizations located in the United States
                  or U.S. territories can use the online application. Foreign
                  organizations must use one of the other methods to apply for
                  an EIN in one of the following ways.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Item E - Group exemption number
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the organization is covered by a group exemption, enter the
                  group exemption number.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Item F - Amended Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box if the organization previously filed a Form
                  990-T return with the IRS for a tax year and is now filing
                  another return for the same tax year to amend the previously
                  filed return.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Item G - Organization type
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the box that describes your organization to the best
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Check the box for &quot;6417(d)(1)(A) Applicable
                    entity&quot; only if no other checkbox on this line applies.
                  </li>
                  <li>
                    “Other trust” includes IRAs, SEPs, SIMPLEs, Roth IRAs,
                    Coverdell ESAs, and Archer MSAs.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Item H -</span> Check the
                  box if you&apos;re filing Form 990-T only to claim a credit
                  from Form 8941, to claim a refund shown on Form 2439, or to
                  claim the elective payment election amount from Form 3800.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Item I -</span> Check if
                  you are a 501(c)(3) organization filing a consolidated return
                  with a 501(c)(2) title holding corporation.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Item J - Schedule A
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total number of Schedules A attached to Form 990-T.
                  An organization with one or more unrelated trades or
                  businesses will complete a separate Schedule A for each
                  unrelated trade or business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Item K -</span> Check “Yes”
                  box if your organization is a corporation and either (1) or
                  (2) below applies.
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The corporation is a subsidiary in an affiliated group
                    (defined in section 1504) but isn&apos;t filing a
                    consolidated return for the tax year with that group.
                  </li>
                  <li>
                    The corporation is a subsidiary in a parent-subsidiary
                    controlled group (defined in section 1563).{' '}
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, mb: 1, fontStyle: 'italic' }}
                >
                  Excluded member
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the corporation is an &quot;excluded member&quot; of a
                  controlled group (see section 1563(b)(2)), it is still
                  considered a member of a controlled group for purposes of item
                  K.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Item L -</span> Enter here
                  the name and address of the person who has the
                  organization&apos;s books and records and the telephone number
                  at which they can be reached.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part I - Total Unrelated Business Taxable Income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990-t-unrelated-img.webp" />
                  <source srcSet="/assets/images/990-t-unrelated-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990-t-unrelated-img.png"
                    loading="lazy"
                    alt="Form 990-T Part I, lines 1–11 showing total unrelated business taxable income"
                    width={721}
                    height={225}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 1 -</span> Enter here
                  the sum of the positive amounts from all Schedules A (Form
                  990-T), Part II, line 18.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, don’t include any amount from Schedule A (Form
                  990-T), Part II, line 18, that is less than zero in the
                  computation of total unrelated trade or business income
                  reported on Part I, line 1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2 -</span> This line
                  is reserved. Do not enter any amount on this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3 -</span> Add lines 1
                  and 2 and enter the sum value here
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4 - Charitable Contributions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter contributions or gifts actually paid within the tax year
                  to or for the use of charitable and governmental organizations
                  described in section 170(c). Also, enter any unused
                  contributions carried over from earlier years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 5 -</span> Subtract
                  line 4 from line 3. The difference value is the Total
                  unrelated business taxable income before net operating losses.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6 - Deduction for Net Operating Loss Arising in Tax Years
                  Beginning Before 2018
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the smaller of (a) the amount of NOL arising in tax
                  years beginning before January 1, 2018, or (b) the amount
                  shown on Part I, line 1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 7 -</span> Subtract
                  line 6 from line 5. The difference value is the Total of
                  unrelated business taxable income before specific deduction
                  and section 199A deduction
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8 - Specific Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  A specific deduction of $1,000 is allowed except for computing
                  the NOL and the net operating loss deduction under section
                  172.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                  An organization can usually claim only{' '}
                  <span style={{ fontWeight: 600 }}>one</span> $1,000 deduction
                  for all its unrelated businesses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However,{' '}
                  <span style={{ fontWeight: 600 }}>
                    a diocese, religious province, or church association
                  </span>
                  can claim{' '}
                  <span style={{ fontWeight: 600 }}>
                    one $1,000 deduction for each local unit
                  </span>{' '}
                  (like a parish or district){' '}
                  <span style={{ fontWeight: 600 }}>if</span> the units are{' '}
                  <span style={{ fontWeight: 600 }}>
                    not separately incorporated
                  </span>
                  .
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The deduction for each unit is the{' '}
                    <span style={{ fontWeight: 600 }}>
                      smaller of $1,000 or the unit’s gross unrelated business
                      income
                    </span>
                    .{' '}
                  </li>
                  <li>
                    If claiming{' '}
                    <span style={{ fontWeight: 600 }}>
                      more than $1,000 total,
                    </span>{' '}
                    you must attach a list showing each unit’s name, income, and
                    deduction.{' '}
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#c026521f',
                    borderLeft: '5px solid #c02652',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                    Important:{' '}
                  </Typography>
                  <Box component="ul" sx={{ mb: 1, '& li ': { mb: 1.2 } }}>
                    <li>
                      The main organization must file a combined return for all
                      its unincorporated units
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}> Incorporated </span>{' '}
                      units must{' '}
                      <span style={{ fontWeight: 600 }}>
                        file their own returns
                      </span>{' '}
                      separately.{' '}
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9 - Trusts
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, mb: 0.5, fontStyle: 'italic' }}
                >
                  Section 199A Deduction (For trust filers only)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you are a trust filing Form 990-T and have unrelated
                  business income, you may have Qualified Business Income (QBI)
                  and may be allowed a QBI deduction under section 199A.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For purposes of calculating the QBI deduction, the taxable
                  income before the QBI deduction is the amount reported on Part
                  I, line 7, minus the amount reported on Part I, line 8.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mt: 1,
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> When an organization has multiple unrelated
                  businesses, it calculates income separately for each one — and
                  losses can&apos;t reduce other income. If a business has a
                  loss, that loss isn’t counted in the current year’s Unrelated
                  Business Taxable Income (UBTI) or Qualified Business Income
                  (QBI). Instead, the loss is carried forward and used in future
                  years when that same business has income. Also, W-2 wages and
                  property values from a business with a loss aren’t used to
                  calculate QBI limits for high-income taxpayers.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10 - Total deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 8 and 9 and enter the sum value in this line.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 11 - Unrelated business taxable income
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Subtract line 10 from line 7. If line 10 is greater than line
                  7, enter zero in this line.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part II - Tax Computation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990-t-computation-img.webp" />
                  <source srcSet="/assets/images/990-t-computation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990-t-computation-img.png"
                    loading="lazy"
                    alt="Form 990-T Part II lines 1–7 tax computation section"
                    width={720}
                    height={162}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1 - Organizations taxable as corporations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply Part I, line 11, by 21% (0.21) and enter the value in
                  this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2 - Trusts taxable at trust rates.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Trusts exempt under section 501(a) are taxed like regular
                  trusts using trust tax rates. This also applies to employee
                  trusts under section 401(a).
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Most trusts calculate tax on UBTI (Unrelated Business
                    Taxable Income) using the Tax Rate Schedule for Trusts.
                  </li>
                  <li>
                    If using the schedule, enter the tax on Part II, line 2 of
                    Form 990-T and check the &quot;Tax rate schedule&quot;
                    box.{' '}
                  </li>
                  <li>
                    If the trust qualifies for special rates on capital gains
                    and dividends, use Schedule D (Form 1041) instead. Enter
                    that tax on Part II, line 2 and check the &quot;Schedule
                    D&quot; box, attaching Schedule D.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> A trust with more than one unrelated trade or
                  business that computes its tax on Schedule D (Form 1041) may
                  need to adjust the amount entered on Schedule D (Form 1041),
                  Part V, line 22, to include only the net gain from Schedule D
                  (Form 1041), line 18a (column 2), or line 19 (column 2), that
                  is included in income on Part I of Form 990-T.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3 - Proxy tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most exempt organizations (except 501(c)(3) groups) must
                  report lobbying expenses on Form 990. If they don&apos;t
                  properly notify members about the part of dues used for
                  lobbying or political activities, they must pay a{' '}
                  <span style={{ fontWeight: 600 }}> proxy tax</span>.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    To pay it: enter the tax on Part II, line 3 of the form and
                    attach a calculation statement.{' '}
                  </li>
                  <li>
                    The proxy tax is 21% of the amount not disclosed in the
                    notices.{' '}
                  </li>
                  <li>No deductions are allowed.</li>
                  <li>
                    See Form 990 instructions and Rev. Proc. 98-19 for details
                    and exceptions.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 4a -</span> Enter the
                  amount from Form 4255, Part I, line 3, column (q).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 4b -</span> This line
                  is intended to capture any positive tax amount that doesn’t
                  have a specific line. An MeF (Internet filing) dependency
                  (attachment) captures the detail. Use line 4 to report tax
                  amounts not reported on a specific line in Part II (excluding
                  tax deferred under section 1294, which is included on Part
                  III, line 4).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Use this line to report:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>
                      base erosion minimum tax
                    </span>{' '}
                    from Form 8991 (for certain large corporations making
                    payments to foreign affiliates).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Tax and interest</span>{' '}
                    from a{' '}
                    <span style={{ fontWeight: 600 }}>
                      nonqualified withdrawal
                    </span>{' '}
                    from a capital construction fund.
                  </li>
                  <li>
                    The <span style={{ fontWeight: 600 }}>deferred tax</span> on
                    an{' '}
                    <span style={{ fontWeight: 600 }}>excess distribution</span>{' '}
                    from a{' '}
                    <span style={{ fontWeight: 600 }}>
                      passive foreign investment company (PFIC)
                    </span>{' '}
                    (from Form 8621).{' '}
                  </li>
                  <li>
                    Any{' '}
                    <span style={{ fontWeight: 600 }}>
                      tax increase from a partner&apos;s audit adjustments
                    </span>
                    (reported using Form 8978). <br />
                    <span style={{ fontStyle: 'italic' }}>
                      (If there’s a decrease, report it separately in Part III.)
                    </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Unless otherwise indicated, when reporting deferred tax on
                  line 4, don&apos;t include interest on the tax amount.
                  Instead, report such interest as an “other amount due” on Part
                  III, line 3. For example, interest on tax deferred under
                  section 1291(c)(1), determined under section 1291(c)(3) is
                  reported on Part III, line 3.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> How to report.</span>{' '}
                  Attach a statement to Part II, line 4, showing (a) a brief
                  description of the type of tax, and (b) the amount. For
                  example, if the organization is reporting $100 of tax due from
                  an increase in tax attributable to a partner&apos;s audit
                  liability (Form 8978), the attachment would show “Form 8978”
                  and “$100.”
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5 - Alternative minimum tax
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Organizations liable for tax on unrelated business taxable
                    income may be liable for alternative minimum tax.
                  </li>
                  <li>
                    Trusts attach Schedule I, Alternative Minimum Tax–Estate and
                    Trusts, and enter any tax from Schedule I on this line.
                  </li>
                  <li>
                    Corporations may need to complete Form 4626, Alternative
                    Minimum Tax–Corporation, and enter any tax from Form 4626 on
                    this line. You may need to file Form 4626 with your tax year
                    2025 Form 990-T.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6 - Tax on Noncompliant Facility Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If a hospital doesn&apos;t meet certain rules, it has to pay a
                  separate income tax on that facility’s income. This is
                  different from the excise tax reported on Form 4720 for not
                  completing a community health needs assessment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 7 -</span> Add Part
                  II, lines 3, 4, 5, and 6, to Part II, line 1 or 2, whichever
                  applies and enter the sum value in this line.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part III - Tax and Payments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990-t-tax-pay-img.webp" />
                  <source srcSet="/assets/images/990-t-tax-pay-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990-t-tax-pay-img.png"
                    loading="lazy"
                    alt="Form 990-T Part III lines 1–4 showing tax and payments"
                    width={721}
                    height={237}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <picture>
                  <source srcSet="/assets/images/990-t-taxpay-III-img.webp" />
                  <source srcSet="/assets/images/990-t-taxpay-III-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990-t-taxpay-III-img.png"
                    loading="lazy"
                    alt="Form 990-T Part III lines 5-11 tax and payments section "
                    width={721}
                    height={289}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1a - Foreign tax credit
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Corporations.</span> See
                    Form 1118, Foreign Tax Credit—Corporations, for an
                    explanation of when a corporation can claim this credit for
                    payment of income tax to a foreign country or U.S.
                    possession.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Trusts.</span> See Form
                    1116, Foreign Tax Credit (Individual, Estate, or Trust), for
                    rules on how the trust computes the foreign tax credit.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete the form that applies to the organization and attach
                  the form to Form 990-T. Enter the credit on this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1b - Other credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this line to report nonrefundable credits that aren’t
                  listed elsewhere in Part III, line 1. Attach a statement
                  showing the form and credit amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Examples of such credits:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Qualified Electric Vehicle Credit from prior years (Form
                    8834, line 7 — attach Form 8834).
                  </li>
                  <li>
                    Credit for holders of tax credit bonds (Form 8912, line 12).
                  </li>
                  <li>
                    Decrease in taxes due from partnership adjustments (Form
                    8978, line 14 — attach <br /> Form 8978).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1c - General Business Credit
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the organization&apos;s total general business credit
                  (excluding the work opportunity credit, the employee retention
                  credit, the empowerment zone employment credit, and the credit
                  for employer differential wage payments).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The organization is required to file Form 3800, General
                  Business Credit, to claim any business credit. For a list of
                  credits, see Form 3800. Include the allowable credit from Form
                  3800, Part II, line 38, on Form 990-T, Part III, line 1c.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d - Credit for prior-year minimum tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use Form 8801 to figure the minimum tax credit and any
                  carryforwards of that credit for trusts. For corporations, use
                  Form 8827.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1d - Credit for prior-year minimum tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Form 8801 to figure the minimum tax credit and any
                  carryforwards of that credit for trusts. For corporations, use
                  Form 8827.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1e - Total Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 1a through 1d and enter the sum value in this line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2 -</span> Subtract
                  line 1e from Part II, line 7 and enter the difference here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3a -</span> Enter the
                  amount from Form 4255, Part I, line 3, column (r).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3b -</span> If the
                  corporation got a low-income housing credit but later disposed
                  of the property or reduced its qualified basis without
                  following proper steps, it might owe a tax. Use{' '}
                  <span style={{ fontWeight: 600 }}> Form 8611</span> to
                  calculate it.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3c -</span> If the
                  corporation used the{' '}
                  <span style={{ fontWeight: 600 }}>
                    percentage-of-completion method
                  </span>{' '}
                  for long-term contracts, it may owe or get interest back. Use{' '}
                  <span style={{ fontWeight: 600 }}>Form 8697</span> to figure
                  it out.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3d -</span> If the
                  corporation used the{' '}
                  <span style={{ fontWeight: 600 }}>
                    income forecast method
                  </span>{' '}
                  to depreciate property, it may owe or get interest back. Use{' '}
                  <span style={{ fontWeight: 600 }}>Form 8866</span> to figure
                  it out.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3e - Other Amounts
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the organization has extra amounts due (like certain types
                  of interest or tax from shipping activities), they should be
                  included in Part III, line 3. Check the “Other” box and attach
                  a statement explaining the details.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Examples include:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Interest on deferred taxes from installment sales of
                    time-shares, residential lots, or nondealer installment
                    obligations.{' '}
                  </li>
                  <li>Interest on deferred gains. </li>
                  <li>
                    Tax on income from qualifying shipping activities (use Form
                    8902).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> How to report -</span> If
                  the organization checks &quot;Other&quot; on Part III, line
                  3e, they must attach a statement that:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Shows how each amount was calculated.</li>
                  <li>Lists: </li>

                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li> the related Code section or form number,</li>{' '}
                    <li>the type of tax or interest, and</li>
                    <li> the amount.</li>
                    <li style={{ fontWeight: 600, listStyleType: 'none' }}>
                      {' '}
                      Example:
                    </li>
                    <li style={{ listStyleType: 'none' }}>
                      {' '}
                      “Section 30 — QEV recapture tax — $100.”{' '}
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4 - Total Tax
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include any deferred tax on the termination of a section 1294
                  election applicable to shareholders in a qualified electing
                  fund (QEF) in the amount entered on Part III, line 4. See Form
                  8621, Part VI, and How to report, later.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract from the total entered on Part III, line 4, any
                  deferred tax on the corporation&apos;s share of undistributed
                  earnings of a QEF. See Form 8621, Part III.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, mb: 0.5, fontStyle: 'italic' }}
                >
                  How to report
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach a statement showing the computation of each item
                  included in, or subtracted from, the total on Part III, line
                  4. Specify (a) the applicable Code section, (b) the type of
                  tax, and (c) the amount of tax.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Line 5 - Section 965
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Corporation:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    After 2021, corporations no longer report new Section 965(a)
                    inclusions
                  </li>{' '}
                  <li>
                    If paying Section 965 tax in installments, attach{' '}
                    <span style={{ fontWeight: 600 }}> Form 965-B</span> to{' '}
                    <span style={{ fontWeight: 600 }}>Form 990-T</span>.{' '}
                  </li>
                  <li>
                    {' '}
                    Pay the current installment separately (using a special
                    voucher sent by mail).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Do not</span> include
                    this payment in Part III (Tax and Payments) of Form
                    990-T.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Trust
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If a trust has current-year Section 965 tax, report it on
                    line 5 using{' '}
                    <span style={{ fontWeight: 600 }}>Form 965-A. </span>
                  </li>{' '}
                  <li>
                    If paying installments (but no new liability), attach{' '}
                    <span style={{ fontWeight: 600 }}>Form 965-A </span>but{' '}
                    <span style={{ fontWeight: 600 }}>do not</span> include the
                    installment in Part III (same as corporations).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6a Payments -</span>{' '}
                  Enter here the preceding year’s overpayment credited to the
                  current year.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6b - Estimated Tax Payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total estimated tax payments made for the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If an organization is the beneficiary of a trust, and the
                  trust makes a section 643(g) election to credit its estimated
                  tax payments to its beneficiaries, include the
                  organization&apos;s share of the estimated tax payments in the
                  total amount entered here. Also, attach a statement showing
                  the amount of the section 643(g) credit amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6c -</span> Enter the
                  tax deposited with{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8868
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6d - Foreign Organizations
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amount of U.S. tax withheld on UBTI (Unrelated
                  Business Taxable Income) that isn&apos;t connected to a U.S.
                  trade or business. Attach{' '}
                  <span style={{ fontWeight: 600 }}> Form 1042-S</span> or a
                  similar form to show proof of the withheld tax reported on
                  Part III, line 6d.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6e - Backup Withholding
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization is exempt but was wrongly subject to
                  backup withholding (because the payer didn’t have your correct
                  tax ID), you can claim a credit for the withheld amount by
                  reporting it on this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6f - Credit for Small Employer Health Insurance Premiums
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A tax-exempt organization under section 501(c) can claim a
                  refundable credit for part of the health insurance premiums it
                  pays for employees. To get the credit, the organization must
                  file Form 8941 and keep records to support the amount claimed.
                </Typography>
                <Alert
                  severity="warning"
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  icon={false}
                >
                  <b>Note:</b> If a tax-exempt eligible small employer is filing
                  Form 990-T only to request a credit for small employer health
                  insurance premiums paid, complete the following steps.
                </Alert>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Fill in the heading (the area above Part I) except items J
                    and K. Check the box for “Credit from Form 8941” in item H .
                  </li>{' '}
                  <li>Enter -0- on Part I, line 11, and Part III, line 4.</li>
                  <li>
                    Enter the credit from Form 8941, line 20, on Part III, line
                    6f.{' '}
                  </li>
                  <li>
                    Complete Part III, lines 7, 10, and 11, and the signature
                    area.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6g - Elective Payment Election
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter here the total net elective payment election amount from
                  Form 3800, Part III, line 6, column (j).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6h - Tax on Undistributed Long-Term Capital Gain by RIC
                  or Reit
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the tax paid by a regulated investment company (RIC) or
                  real estate investment trust (REIT) on any undistributed
                  long-term capital gains. You need to attach each Form 2439 you
                  received from the RIC or REIT where you hold shares. If
                  you&apos;re filing a composite Form 990-T, refer to the
                  &quot;Composite Form 990-T&quot; section for specific
                  instructions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6i - Credit for Federal Excise Tax Paid on Fuels
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If your organization paid a federal excise tax on certain
                  fuels and qualify for any of the credits listed below, attach
                  Form 4136 to your return and enter the total credit on line
                  6i.
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>The biodiesel or renewable diesel mixture credit.</li>{' '}
                  <li>The alternative fuel credit. </li>
                  <li>
                    A credit for certain nontaxable uses (or sales) of fuel
                    during your income tax year.
                  </li>
                  <li>A credit for blending a diesel-water fuels emulsion.</li>
                  <li>
                    A credit for exporting dyed fuels or gasoline
                    blendstocks.{' '}
                  </li>
                  <li>Sustainable aviation fuel (SAF) credit. </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6j - Other Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For other credits, check the “Other” box and provide the
                  following information:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The number of the form used to calculate the credit, or the
                    code section that establishes the credit,{' '}
                  </li>{' '}
                  <li>A brief description of the credit, and </li>
                  <li>The amount of the credit.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If necessary, provide information required to claim a specific
                  credit in Part V, Supplemental Information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Other credits may include the following:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The credit for ozone-depleting chemicals. Include any credit
                    the organization is claiming under section 4682(g) for taxes
                    paid on chemicals used as propellants in metered-dose
                    inhalers.
                  </li>{' '}
                  <li>
                    The amount of current year net section 965 tax liability,
                    For a trust, this amount will be from Form 956-A, Part I,
                    column (d), line 4.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  icon={false}
                >
                  <b>Note -</b> Do not use Part III, line 6j, to claim a refund
                  of federal tax withheld and shown a 1099 form. Claims for
                  refund of backup withholding should be shown on Part III, line
                  6e.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7 - Total payments
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines 6a through 6j and enter the sum value in this line.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 8 - Estimated Tax Penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use <span style={{ fontWeight: 600 }}> Form 2220</span> to
                  check if your organization owes a penalty for underpaying
                  estimated taxes. Normally, you don’t need to file it because
                  the IRS will calculate any penalty. But you must complete and
                  attach Form 2220 if:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    You’re using the annualized income or seasonal installment
                    method, <span style={{ fontWeight: 600 }}> or</span>
                  </li>{' '}
                  <li>
                    You’re a large organization using last year’s tax to figure
                    your first payment.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you attach Form 2220,{' '}
                  <span style={{ fontWeight: 600 }}>
                    check the box on Form 990-T, Part III, line 8, and enter the
                    penalty amount
                  </span>{' '}
                  there.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 9 - Tax Due
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must pay the tax in full when the return is filed. You may
                  pay by EFTPS. For more information about EFTPS, see Electronic
                  Deposit Requirement, earlier. Also, you may pay by credit or
                  debit card.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 10 - Overpayment
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If line 7 is larger than the total of lines 4, 5, and 8, enter
                  here the amount overpaid.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 11 -</span> Enter the
                  amount of line 10 you want: Credited to 2026 estimated tax.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Note:</span> If your
                  organization has an overpayment when filing Form 990-T, you
                  may choose to receive the refund through direct deposit by
                  attaching Form 8050. To learn how to complete the form, refer
                  to our{' '}
                  <Link
                    href="/resources/tax-exempt/form-8050-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 8050 instructions
                  </Link>{' '}
                  page.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part IV - Statements Regarding Certain Activities and Other
                  Information
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/990-t-statement-img.webp" />
                  <source srcSet="/assets/images/990-t-statement-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990-t-statement-img.png"
                    loading="lazy"
                    alt="Form 990-T Part IV lines 1–6b statements regarding certain activities and other information"
                    width={720}
                    height={322}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 1 -</span> Check “Yes”
                  if either item (1) or (2) below applies.
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    At any time during the year the organization had an interest
                    in or signature or other authority over a financial account
                    in a foreign country (such as a bank account, securities
                    account, or other financial account); and
                  </li>{' '}
                  <ul style={{ listStyleType: 'lower-alpha' }}>
                    <li>
                      The combined value of the accounts was more than $10,000
                      at any time during the year; and{' '}
                    </li>
                    <li>
                      The accounts were not with a U.S. military banking
                      facility operated by a U.S. financial institution.
                    </li>
                  </ul>
                  <li>
                    The organization owns more than 50% of the stock in any
                    corporation that would answer “Yes” to item (1).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the “Yes” box is checked, write the name of the foreign
                  country or countries. If the list of foreign country names
                  will not fit in the available space, continue the list in Part
                  V, Supplemental Information.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check if the organization has control over or an interest in a
                  foreign financial account. If it does, file FinCEN Form 114
                  (FBAR) electronically through the BSA E-Filing System — not
                  with Form 990-T, since it&apos;s not a tax form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2 -</span> The
                  organization may be required to file Form 3520, Annual Return
                  To Report Transactions With Foreign Trusts and Receipt of
                  Certain Foreign Gifts, if either of the following applies.
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    It directly or indirectly transferred money or property to a
                    foreign trust. For this purpose, any U.S. person who created
                    a foreign trust is considered a transferor.
                  </li>{' '}
                  <li>
                    It is treated as the owner of any part of the assets of a
                    foreign trust under the grantor trust rules.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3 -</span> Report any
                  tax-exempt interest received or accrued in the space provided.
                  Include any exempt-interest dividends received as a
                  shareholder in a mutual fund or other RIC.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 4 -</span> Use line 4
                  to show the amount of the NOL carryover to the tax year from
                  tax years prior to 2018 (“pre-2018 NOL”), even if some of the
                  loss is used to offset income on this return. The amount to
                  enter is the total of all pre-2018 NOLs generated in any year
                  prior to 2018, and not used to offset income (either as a
                  carryback or carryover) to a tax year prior to 2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, do not reduce the amount by any NOL deduction
                  reported on Part I, line 6.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Line 5 -</span> In this
                  line, list each NOL (Net Operating Loss) from separate trades
                  or businesses after 2017.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    In the <span style={{ fontWeight: 600 }}>first column</span>
                    , write the business activity code for each business.{' '}
                  </li>
                  <li>
                    In the{' '}
                    <span style={{ fontWeight: 600 }}>second column</span>,
                    write the{' '}
                    <span style={{ fontWeight: 600 }}>
                      full unused NOL amount
                    </span>{' '}
                    for each business (from after 2017 and before 2025).{' '}
                  </li>
                  <li style={{ fontWeight: 600 }}>Important: </li>
                  <ul>
                    <li>
                      Include all businesses, even if you didn’t file a Schedule
                      A for them this year.{' '}
                    </li>
                    <li>
                      Report the total available NOL — don&apos;t reduce it by
                      what you’re using this year.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Also, if a business activity code changes, special rules apply
                  for its NOLs (check the &quot;Separate Trades or
                  Businesses&quot; section for details).{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Lines 6a and 6b -</span>{' '}
                  are reserved for future use.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part V - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/990-t-supplement-img.webp" />
                  <source srcSet="/assets/images/990-t-supplement-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/990-t-supplement-img.png"
                    loading="lazy"
                    alt="Form 990-T Part V providing supplemental information"
                    width={722}
                    height={209}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Part V to provide the IRS with narrative information
                  required for responses to specific questions on Form 990-T,
                  and to explain the organization’s operations or responses to
                  various questions.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Signature
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Corporations:</span> The
                  return must be signed and dated by a corporate officer (e.g.,
                  president, treasurer, or tax officer). If a receiver, trustee,
                  or assignee is filing, they must sign too.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Trusts:</span> The return
                  must be signed and dated by the fiduciary or an authorized
                  officer. If there are multiple fiduciaries, any one of them
                  can sign.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    IRA Trusts (Special Rule):
                  </span>{' '}
                  Trustees can use a facsimile (stamped) signature if they
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Send a signed letter to the IRS confirming the use of the
                    facsimile.{' '}
                  </li>
                  <li>List all returns with trust names and EINs. </li>
                  <li>
                    Make no changes to the return after signing (except
                    correcting math errors){' '}
                  </li>
                  <li>
                    Keep a manually signed copy of the letter and corrections
                    for IRS inspection.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Paid preparer
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Anyone who is paid to prepare the return must sign the return,
                  list the preparer taxpayer identification number (PTIN), and
                  fill in the other blanks in the Paid Preparer Use Only area
                  unless that person is paid for preparation as part of their
                  duties as your employee
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  The paid preparer must:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Sign the return in the space provided for the
                    preparer&apos;s signature;
                  </li>
                  <li>
                    Include their Preparer Tax Identification Number (PTIN);
                    and{' '}
                  </li>
                  <li>Give a copy of the return to the organization.</li>
                </Box>
                <Alert
                  severity="warning"
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  icon={false}
                >
                  <b>Note -</b> A paid preparer may sign original returns,
                  amended returns, or requests for filing extensions by rubber
                  stamp, mechanical device, or computer software program. Also,
                  facsimile signatures are authorized.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Paid Preparer Authorization
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the organization checks &quot;Yes&quot; in the signature
                  section, it allows the IRS to discuss the return with the
                  individual paid preparer who signed it (not the whole firm).
                  The IRS can:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Ask the preparer for missing info,</li>
                  <li>Give updates about processing, refund, or payment,</li>
                  <li>Talk about certain notices (like math errors). </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Limits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The preparer can&apos;t get refunds, make decisions for the
                  organization, or represent it before the IRS. The permission
                  ends automatically by the due date of next year’s Form 990-T
                  and can&apos;t be revoked early. For broader authorization,
                  see IRS Pub. 947, Practice Before the IRS and Power of
                  Attorney.
                </Typography>
                <Alert
                  severity="warning"
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  icon={false}
                >
                  <b>Note -</b> Enter the paid preparer’s Preparer Tax
                  Identification Number (PTIN), not their SSN, in the “PTIN” box
                  in the paid preparer’s block. Because Form 990-T is publicly
                  disclosable when filed by a 501(c)(3) organization, any
                  information entered in this block will be publicly disclosed.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Mailing address of Form 990-T
                </Typography>
                <Typography variant="body1">
                  Send a paper copy of Form 990-T to the below mailing
                  address:{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 3 }}>
                  Department of the Treasury,
                  <br />
                  Internal Revenue Service Center, <br />
                  Ogden, UT 84201-0027.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-file Form 990-T with TaxZerone?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Filing{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-t/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    990-T form
                  </Link>{' '}
                  with TaxZerone takes 3 simple steps. Follow these steps for an
                  easy filing!
                </Typography>{' '}
                <Box
                  component="ul"
                  sx={{
                    mb: 3,
                    listStyleType: 'none',
                    mr: 2,
                    '& li': { mb: 1.8 },
                  }}
                >
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> Step 1:</span> Enter the
                    required Organization information manually or use{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_990FINDER_SITE_URL}?ref=990Tinstructions`}
                      sx={{ color: '#0202ff' }}
                    >
                      990 EIN Finder
                    </Link>{' '}
                    to fetch your organization’s information from the previous
                    filings.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> Step 2:</span> Complete
                    all the necessary parts provided in the form and the
                    required
                    <Link
                      href="/resources/tax-exempt/form-990-t-schedule-a/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      schedules for Form 990-T
                    </Link>{' '}
                    will be automatically added by TaxZerone{' '}
                    <span style={{ fontWeight: 600 }}>at no extra cost</span>.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> Step 3:</span> Review the
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      990-T
                    </Link>{' '}
                    return and transmit to the IRS.
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Looking to file Form 990-T online?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your IRS Form 990-T electronically through TaxZerone for just{' '}
              <b>$129.99 per return</b>.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990Tinstructions`}
              >
                E-File Form 990-T Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form990TInstructions;
